import type { GroupAttendeeView } from '@ehr/shared';
import {
  DRAFT_FIELDS,
  draftNoteSections,
  mergeDrafts,
  type NpContext,
  type NpOptions,
} from './notepilot.js';

/**
 * Group workspace rules (calendar#46 step 8, PROGRESS_NOTE_GROUP_A-3 readiness /
 * submitPlan), kept pure so the rail, the counts and the Submit dialog agree.
 *
 * One change from the mockup, per the #46 decisions: a note that still holds an
 * unedited NotePilot draft is held back (the server refuses it too), not sent with a
 * warning.
 */

export type Body = Record<string, unknown>;
export type ReadyKind =
  'notstarted' | 'missing' | 'draftonly' | 'changes' | 'ready' | 'pending' | 'cosigned';
export interface Readiness {
  kind: ReadyKind;
  text: string;
  cls: '' | 'ok' | 'warn' | 'bad';
}

// Template sections, in order: [body key, label].
const TEXT_FIELDS: Record<string, [string, string][]> = {
  SOAP: [
    ['subjective', 'Subjective'],
    ['objective', 'Objective'],
    ['assessment', 'Assessment'],
    ['plan', 'Plan'],
  ],
  DAP: [
    ['data', 'Data'],
    ['assessment', 'Assessment'],
    ['plan', 'Plan'],
  ],
  BIRP: [
    ['behavior', 'Behavior'],
    ['intervention', 'Intervention'],
    ['response', 'Response'],
    ['plan', 'Plan'],
  ],
};

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const list = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);
const hasDx = (b: Body) => Array.isArray(b.diagnoses) && b.diagnoses.length > 0;

/** Labels of the template's empty sections; 'all' when nothing is written. */
export function emptySections(b: Body, format: string): string[] | 'all' {
  let empty: string[];
  let total: number;
  if (format === 'CREDIFY') {
    const req: [string, boolean][] = [
      ['Presenting concerns', !list(b.concerns).length && !str(b.concernsOther)],
      ['Interventions used', !list(b.interventions).length && !str(b.interventionsOther)],
      ['Client response', !str(b.clientResponse) && !str(b.clientResponseOther)],
      ['Objective', !str(b.objective)],
      ['Assessment', !str(b.assessment)],
      ['Next steps', !list(b.nextSteps).length && !str(b.nextStepsOther)],
    ];
    empty = req.filter(([, e]) => e).map(([l]) => l);
    total = req.length;
  } else {
    const f = TEXT_FIELDS[format] ?? TEXT_FIELDS.SOAP!;
    empty = f.filter(([k]) => !str(b[k])).map(([, l]) => l);
    total = f.length;
  }
  if (empty.length === total) return 'all';
  // Diagnosis codes are required; a missing one is listed first but never makes a
  // note "Not started" on its own.
  return hasDx(b) ? empty : ['Diagnosis codes', ...empty];
}

/** Sections still holding an unedited NotePilot draft. */
export function uneditedDrafts(b: Body, format: string): string[] {
  const drafted = list(b.npDrafted);
  const keys =
    format === 'CREDIFY'
      ? ['objective', 'assessment']
      : (TEXT_FIELDS[format] ?? []).map(([k]) => k);
  return keys.filter((k) => drafted.includes(k) && str(b[k]));
}

export function readiness(
  a: GroupAttendeeView,
  format: string,
  body: Body,
  reviewerFirst: string,
): Readiness {
  const st = a.note?.status ?? 'draft';
  if (st === 'pending') return { kind: 'pending', text: 'With ' + reviewerFirst, cls: '' };
  if (st === 'cosigned') return { kind: 'cosigned', text: 'Co-signed', cls: 'ok' };
  // ehr#189: signed off by the clinician alone (Supervision off): final, like co-signed.
  if (st === 'signed') return { kind: 'cosigned', text: 'Signed', cls: 'ok' };
  const missing = emptySections(body, format);
  if (missing === 'all') return { kind: 'notstarted', text: 'Not started', cls: 'warn' };
  if (missing.length)
    return {
      kind: 'missing',
      text:
        missing.length >= 3 ? `${missing.length} sections empty` : `${missing.join(', ')} empty`,
      cls: 'warn',
    };
  if (uneditedDrafts(body, format).length)
    return { kind: 'draftonly', text: 'NotePilot draft, not edited', cls: 'warn' };
  if (st === 'changes')
    return { kind: 'changes', text: '1 note from ' + reviewerFirst, cls: 'bad' };
  return { kind: 'ready', text: 'Ready to sign off', cls: 'ok' };
}

/**
 * ehr#113: why a note is held back, one chip each ("diagnosis codes missing", "assessment
 * empty"), for the Sign Off All window.
 */
export function heldChips(r: Readiness, body: Body, format: string): string[] {
  if (r.kind === 'missing') {
    const m = emptySections(body, format);
    if (m !== 'all' && m.length)
      return m.map((l) =>
        l === 'Diagnosis codes' ? 'diagnosis codes missing' : `${l.toLowerCase()} empty`,
      );
  }
  if (r.kind === 'notstarted') return ['not started'];
  return [r.text];
}

export interface SubmitPlan {
  ready: GroupAttendeeView[];
  resubmit: GroupAttendeeView[];
  held: { a: GroupAttendeeView; r: Readiness }[];
  withReviewer: GroupAttendeeView[];
  cosigned: GroupAttendeeView[];
}

export function submitPlan(
  list: GroupAttendeeView[],
  format: string,
  bodyOf: (a: GroupAttendeeView) => Body,
  reviewerFirst: string,
): SubmitPlan {
  const p: SubmitPlan = { ready: [], resubmit: [], held: [], withReviewer: [], cosigned: [] };
  for (const a of list) {
    const r = readiness(a, format, bodyOf(a), reviewerFirst);
    if (r.kind === 'pending') p.withReviewer.push(a);
    else if (r.kind === 'cosigned') p.cosigned.push(a);
    else if (r.kind === 'ready' || r.kind === 'changes') {
      p.ready.push(a);
      if (a.note?.status === 'changes') p.resubmit.push(a);
    } else p.held.push({ a, r });
  }
  return p;
}

/** "5 ready · 3 unfinished · 2 with Marcus · 1 signed off" (the rail line). */
export function summaryText(p: SubmitPlan, reviewerFirst: string): string {
  const parts = [`${p.ready.length} ready`];
  if (p.held.length) parts.push(`${p.held.length} unfinished`);
  if (p.withReviewer.length) parts.push(`${p.withReviewer.length} with ${reviewerFirst}`);
  if (p.cosigned.length) parts.push(`${p.cosigned.length} signed off`);
  return parts.join(' · ');
}

/** "5 of 12 ready to sign off · 3 held back until finished · 3 locked" (the action bar). */
export function barText(p: SubmitPlan, total: number): string {
  let t = `${p.ready.length} of ${total} ready to sign off`;
  if (p.held.length) t += ` · ${p.held.length} held back until finished`;
  const locked = p.withReviewer.length + p.cosigned.length;
  if (locked) t += ` · ${locked} locked`;
  return t;
}

/** The structured fields NotePilot drafts one attendee's note from. */
export function attendeeContext(
  body: Body,
  format: string,
  s: { durationMin: number; approaches: string[]; clientName?: string; sessionLabel?: string },
): NpContext {
  const cred = format === 'CREDIFY';
  const withOther = (picks: unknown, other: unknown) =>
    cred ? [...list(picks), ...(str(other) ? [str(other)] : [])] : [];
  const dx = Array.isArray(body.diagnoses)
    ? (body.diagnoses as { code?: unknown; label?: unknown }[])
        .filter((d) => typeof d.code === 'string')
        .map((d) => ({ code: String(d.code), label: typeof d.label === 'string' ? d.label : '' }))
    : [];
  return {
    session: `${s.durationMin}-minute ${(s.sessionLabel ?? 'group').toLowerCase()} session`,
    goals: [],
    concerns: withOther(body.concerns, body.concernsOther),
    interventions: withOther(body.interventions, body.interventionsOther),
    nextSteps: withOther(body.nextSteps, body.nextStepsOther),
    response: cred ? str(body.clientResponse) || str(body.clientResponseOther) : '',
    approaches: s.approaches,
    diagnoses: dx,
    clientName: s.clientName,
  };
}

/**
 * ehr#688 G5: group notes draft with Jay's strict rules (GROUP_NOTE_10-29-V25 V21 22a, V22, V23).
 * Individual, family and Service notes keep the default drafter until ehr#688 question 3.
 */
export const GROUP_DRAFT_OPTIONS: NpOptions = { strict: true };

/**
 * #690 follow-up: what the single-note editor's Generate Notes card drafts with. A group member's
 * note (its own card on the group page, or the note opened alone) drafts as Draft everyone does:
 * strict, with the member's name. Every other note (individual, family / couples, Service) keeps
 * the default drafter and its context untouched; strict on family is still open (#689 Q5).
 */
export function noteDraftArgs(
  ctx: NpContext,
  note: { groupMember: boolean; clientName: string },
): [NpContext, NpOptions] {
  if (!note.groupMember) return [ctx, {}];
  return [{ ...ctx, clientName: note.clientName }, GROUP_DRAFT_OPTIONS];
}

/**
 * Draft one attendee's note from the session summary: fills empty sections and
 * refreshes NotePilot's own drafts, never touches text the clinician wrote. Returns
 * the new body and how many sections it wrote.
 *
 * ehr#689 F7 (COUPLES_AND_FAMILY_NOTES_10-29-V15): a family attendee's note passes `options: {}`
 * (the default drafter, Jay 10-30 answer 5: no strict rules, no "Client said" naming), and Apply to
 * <name>'s note passes `refresh: false` (empty boxes only: AI text nobody reviewed stays too).
 * Group callers leave both out: strict, and NotePilot's unreviewed text refreshes.
 */
export function draftAttendee(
  body: Body,
  format: string,
  summary: string,
  s: { durationMin: number; approaches: string[]; clientName?: string; sessionLabel?: string },
  o: { options?: NpOptions; refresh?: boolean } = {},
): { body: Body; drafted: number } {
  const fields = DRAFT_FIELDS[format] ?? [];
  const current: Record<string, string> = {};
  for (const k of fields) current[k] = typeof body[k] === 'string' ? (body[k] as string) : '';
  const had = new Set(list(body.npDrafted));
  const r = mergeDrafts(
    draftNoteSections(summary, attendeeContext(body, format, s), o.options ?? GROUP_DRAFT_OPTIONS),
    fields,
    current,
    o.refresh === false ? new Set<string>() : had,
  );
  if (!r.drafted) return { body, drafted: 0 };
  return {
    body: { ...body, ...r.writes, npDrafted: [...new Set([...had, ...Object.keys(r.writes)])] },
    drafted: r.drafted,
  };
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
export const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

/**
 * ehr#688 (GROUP_NOTE_10-29-V25 groupAiBody): whether this person can change a member's note from
 * the group bar (Generate everyone, Apply to <name>'s note). Not yet written, or a draft or returned
 * note they wrote (an admin or manager may write any); a note with the reviewer, signed or
 * co-signed is locked, and someone else's draft is theirs to write (the editor shows it read-only).
 */
export function memberNoteOpen(
  a: Pick<GroupAttendeeView, 'note'>,
  me: string | null,
  isManager: boolean,
): boolean {
  if (!a.note) return true;
  if (a.note.status !== 'draft' && a.note.status !== 'changes') return false;
  return isManager || a.note.authorId === me;
}

/**
 * ehr#688 (V25 groupAiBody, V22 read-only): why Generate everyone is off, or null when it can run.
 * Only the group's lead clinician (or an admin or manager) generates everyone, as only they write
 * the group's notes; never in a read-only copy; and not when no member's note is open to change.
 */
export function generateEveryoneBlock(o: {
  lead: boolean;
  readOnly?: boolean;
  attendees: readonly Pick<GroupAttendeeView, 'note' | 'rosterStatus'>[];
  me: string | null;
  isManager: boolean;
}): string | null {
  if (o.readOnly) return 'This copy is read-only. Use Open here instead to generate notes.';
  if (!o.lead) return 'Only the group’s lead clinician can generate everyone.';
  const open = o.attendees.filter(
    (a) => a.rosterStatus !== 'removed' && memberNoteOpen(a, o.me, o.isManager),
  );
  if (!open.length) return 'Every member’s note is signed, with the reviewer or read-only.';
  return null;
}

/**
 * ehr#688 (GROUP_NOTE_10-29-V25, V21 22a "shows red as missing"): the member note's boxes to paint
 * red. Generate never makes up a line on a group note (GROUP_DRAFT_OPTIONS), so a box the notes say
 * nothing about stays empty; each such box that sign-off still needs (a `sec-<box>` item in the
 * "left to sign" list, with the box empty) is returned by its key. Other items on the list (times,
 * billing, Participation, "Take out <name>", "Review AI text: ...") are not empty boxes and stay as
 * they are. Only for the note's writer while it can be changed: the caller passes an empty list
 * otherwise (signed, with the reviewer, a read-only copy).
 */
export function missingBoxes(
  left: readonly { target: string }[],
  text: Readonly<Record<string, string>>,
): Set<string> {
  const out = new Set<string>();
  for (const c of left) {
    if (!c.target.startsWith('sec-')) continue;
    const key = c.target.slice(4);
    if (!(text[key] ?? '').trim()) out.add(key);
  }
  return out;
}
